import { useEffect, useRef } from 'react';

const ACCESSIBILITY_CDN_BASE_URL = 'https://media.appcharge.com/accessibility';

interface UseAccessibilityProps {
  publisherId: string | undefined;
  enabled?: boolean;
}

export const useAccessibility = ({ publisherId, enabled }: UseAccessibilityProps) => {
  const initializingRef = useRef(false);

  useEffect(() => {
    if (!publisherId || !enabled) {
      return;
    }

    if (initializingRef.current) {
      return;
    }

    const existingScript = document.querySelector(
      `script[data-accessibility-publisher="${publisherId}"]`
    );
    if (existingScript) {
      return;
    }

    initializingRef.current = true;

    const loadAccessibilityScript = async () => {
      try {
        const scriptUrl = `${ACCESSIBILITY_CDN_BASE_URL}/${publisherId}/accessibility-code.js`;
 
        const response = await fetch(scriptUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch accessibility script: ${response.status}`);
        }

        const scriptContent = await response.text();

        if (!scriptContent.trim()) {
          console.debug('Accessibility script is empty');
          return;
        }

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.textContent = scriptContent;
        script.setAttribute('data-accessibility-publisher', publisherId);
        document.body.appendChild(script);
      } catch (error) {
        console.warn('Failed to load accessibility script:', error);
        initializingRef.current = false; 
      }
    };

    loadAccessibilityScript();
  }, [publisherId, enabled]);
};

export default useAccessibility;
